// components/turnier/AusschreibungEditor.jsx — Schritt „Ausschreibung“ im
// Turnier-Assistenten und Reiter „Ausschreibung“ in der vollen Maske
// (22.10.2026, Muster docs/muster-ausschreibung).
//
// Lädt und speichert selbst über /api/ausschreibung/:id (Server prüft alles,
// hier steht nur, was er antwortet; jede Antwort mit res.ok geprüft).
// Haftung, Datenschutz, Versicherung: Knopf „aus Verbandsvorlage übernehmen“
// (Vorlage in Turnier-Einstellungen → Ausschreibungsvorlagen).
// Alles hier ist für die öffentliche Turnierseite gedacht; leere Felder
// erscheinen dort nicht.
import React, { useCallback, useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { dateiHolen } from '../../lib/dateiHolen';
import '../../styles/TurnierAblauf.css';
import './AusschreibungEditor.css';

const LEER_FELDER = {
  veranstalter_name: '', ausrichter_name: '', wettkampfleitung: '', halle_name: '', einlass_zeit: '',
  parken: '', oepnv: '', barrierefrei: '', zahlungsweise: '', zahlungsfrist: '', abmelde_regel: '',
  startberechtigung: '', pass_pflicht: '', altersnachweis: '', haftung_text: '', versicherung_text: '', datenschutz_text: '',
  kampfrichter_regel: '', kampfrichter_ausfallgebuehr: '', coach_regel: '', zuschauer_eintritt: '', verpflegung: '',
  protestgebuehr: '', ummeldegebuehr: '',
};
const NEU = {
  programm: () => ({ tag: '', von: '', bis: '', titel: '', flaeche: '', art: 'ablauf', text: '' }),
  kampfzeiten: () => ({ disziplin: '', klasse: '', zeit: '' }),
  schutz: () => ({ disziplin: '', ausruestung: '' }),
  hotels: () => ({ name: '', entfernung: '', hinweis: '', link: '' }),
};
const ART_TEXT = { ablauf: 'Ablauf', wettkampf: 'Wettkampf', siegerehrung: 'Siegerehrung' };

async function antwort(res) {
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.success === false) throw new Error(d.error || `Fehler ${res.status}`);
  return d;
}
const kopf = (json = true) => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`, ...(json ? { 'Content-Type': 'application/json' } : {}),
});
const groesse = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function Feld({ label, hilfe, breit, children }) {
  return (
    <label className={`tw-feld${breit ? ' tw-breit' : ''}`}>
      {label}
      {children}
      {hilfe && <span className="ta-klein">{hilfe}</span>}
    </label>
  );
}

const AusschreibungEditor = forwardRef(function AusschreibungEditor({ turnierId, eigenerKnopf = false }, ref) {
  const [felder, setFelder] = useState(LEER_FELDER);
  const [listen, setListen] = useState({ programm: [], kampfzeiten: [], schutz: [], hotels: [] });
  const [dokumente, setDokumente] = useState([]);
  const [datum, setDatum] = useState('');
  const [geladen, setGeladen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [hinweis, setHinweis] = useState('');
  const [upload, setUpload] = useState({ datei: null, titel: '', oeffentlich: true });
  const [uploadSchluessel, setUploadSchluessel] = useState(0);

  const uebernehmen = (d) => {
    setFelder({ ...LEER_FELDER, ...Object.fromEntries(Object.keys(LEER_FELDER).map((k) => [k, d.felder?.[k] ?? ''])) });
    setListen({
      programm: (d.programm || []).map((p) => ({ ...NEU.programm(), ...p, bis: p.bis || '', von: p.von || '', tag: p.tag || '', text: p.text || '', flaeche: p.flaeche || '' })),
      kampfzeiten: (d.kampfzeiten || []).map((k) => ({ ...NEU.kampfzeiten(), ...k, disziplin: k.disziplin || '' })),
      schutz: d.schutz || [],
      hotels: (d.hotels || []).map((h) => ({ ...NEU.hotels(), ...h, entfernung: h.entfernung || '', hinweis: h.hinweis || '', link: h.link || '' })),
    });
    setDokumente(d.dokumente || []);
    setDatum(d.datum || '');
  };

  const laden = useCallback(async () => {
    if (!turnierId) return;
    try {
      const d = await antwort(await fetch(`/api/ausschreibung/${turnierId}`, { headers: kopf() }));
      uebernehmen(d.data);
      setGeladen(true);
    } catch (e) { setFehler(e.message); }
  }, [turnierId]);
  useEffect(() => { laden(); }, [laden]);

  const speichern = useCallback(async () => {
    setBusy(true); setFehler(''); setHinweis('');
    try {
      const d = await antwort(await fetch(`/api/ausschreibung/${turnierId}`, {
        method: 'PUT', headers: kopf(), body: JSON.stringify({ felder, ...listen }),
      }));
      uebernehmen(d.data);
      setHinweis('Ausschreibung gespeichert.');
      return true;
    } catch (e) { setFehler(e.message); return false; } finally { setBusy(false); }
  }, [turnierId, felder, listen]);
  useImperativeHandle(ref, () => ({ speichern }), [speichern]);

  const set = (k, v) => setFelder((f) => ({ ...f, [k]: v }));
  const zeile = (liste, i, k, v) => setListen((l) => ({ ...l, [liste]: l[liste].map((z, j) => (j === i ? { ...z, [k]: v } : z)) }));
  const dazu = (liste) => setListen((l) => ({ ...l, [liste]: [...l[liste], { ...NEU[liste](), ...(liste === 'programm' ? { tag: datum } : {}) }] }));
  const weg = (liste, i) => setListen((l) => ({ ...l, [liste]: l[liste].filter((_, j) => j !== i) }));
  const schieben = (liste, i, r) => setListen((l) => {
    const a = [...l[liste]]; const j = i + r;
    if (j < 0 || j >= a.length) return l;
    [a[i], a[j]] = [a[j], a[i]];
    return { ...l, [liste]: a };
  });

  const vorlageHolen = async () => {
    setFehler(''); setHinweis('');
    try {
      const v = (await antwort(await fetch(`/api/ausschreibung/${turnierId}/vorlage`, { headers: kopf() }))).data || {};
      const felderV = ['haftung_text', 'versicherung_text', 'datenschutz_text'].filter((k) => (v[k] || '').trim());
      if (!felderV.length) { setHinweis('Für diesen Verband ist noch keine Vorlage hinterlegt (Turnier-Einstellungen → Ausschreibungsvorlagen).'); return; }
      const ueberschreibt = felderV.filter((k) => (felder[k] || '').trim() && felder[k] !== v[k]);
      if (ueberschreibt.length && !window.confirm('Vorhandene Texte zu Haftung, Versicherung oder Datenschutz werden durch die Verbandsvorlage ersetzt. Fortfahren?')) return;
      setFelder((f) => ({ ...f, ...Object.fromEntries(felderV.map((k) => [k, v[k]])) }));
      setHinweis('Vorlage übernommen. Bitte prüfen und speichern.');
    } catch (e) { setFehler(e.message); }
  };

  const hochladen = async () => {
    if (!upload.datei) { setFehler('Bitte eine PDF-Datei wählen.'); return; }
    setBusy(true); setFehler(''); setHinweis('');
    try {
      const fd = new FormData();
      fd.append('datei', upload.datei);
      fd.append('titel', upload.titel);
      fd.append('oeffentlich', upload.oeffentlich ? '1' : '0');
      const d = await antwort(await fetch(`/api/ausschreibung/${turnierId}/dokumente`, { method: 'POST', headers: kopf(false), body: fd }));
      setDokumente(d.dokumente || []);
      setUpload({ datei: null, titel: '', oeffentlich: true });
      setUploadSchluessel((n) => n + 1);
      setHinweis('Anhang hochgeladen.');
    } catch (e) { setFehler(e.message); } finally { setBusy(false); }
  };
  const dokAendern = async (dok, aenderung) => {
    setFehler('');
    try {
      const d = await antwort(await fetch(`/api/ausschreibung/${turnierId}/dokumente/${dok.id}`, { method: 'PATCH', headers: kopf(), body: JSON.stringify(aenderung) }));
      setDokumente(d.dokumente || []);
    } catch (e) { setFehler(e.message); }
  };
  const dokLoeschen = async (dok) => {
    if (!window.confirm(`Anhang „${dok.titel}“ löschen?`)) return;
    setFehler('');
    try {
      const d = await antwort(await fetch(`/api/ausschreibung/${turnierId}/dokumente/${dok.id}`, { method: 'DELETE', headers: kopf() }));
      setDokumente(d.dokumente || []);
    } catch (e) { setFehler(e.message); }
  };
  const dokAnsehen = (dok) => dateiHolen(`/api/ausschreibung/${turnierId}/dokumente/${dok.id}/datei`, { oeffnen: true }).catch((e) => setFehler(e.message));

  if (!turnierId) return <p className="ta-klein">Die Ausschreibung lässt sich ausfüllen, sobald das Turnier gespeichert ist.</p>;
  if (!geladen && !fehler) return <p className="ta-klein">Lädt …</p>;

  const text = (k, label, hilfe, zeilen = 2) => (
    <Feld label={label} hilfe={hilfe} breit><textarea rows={zeilen} value={felder[k]} onChange={(e) => set(k, e.target.value)} /></Feld>
  );
  const eingabe = (k, label, props = {}, hilfe) => (
    <Feld label={label} hilfe={hilfe}><input value={felder[k]} onChange={(e) => set(k, e.target.value)} {...props} /></Feld>
  );
  const listenKnoepfe = (liste, i, n) => (
    <span className="au-ed-knoepfe">
      <button type="button" className="ta-btn ta-btn-leise" aria-label="nach oben" disabled={i === 0} onClick={() => schieben(liste, i, -1)}>↑</button>
      <button type="button" className="ta-btn ta-btn-leise" aria-label="nach unten" disabled={i === n - 1} onClick={() => schieben(liste, i, 1)}>↓</button>
      <button type="button" className="ta-btn ta-btn-leise" onClick={() => weg(liste, i)}>Entfernen</button>
    </span>
  );

  return (
    <div className="au-ed">
      <p className="ta-klein">Alles hier steht auf der öffentlichen Turnierseite, sobald das Turnier veröffentlicht ist. Leere Felder erscheinen dort nicht.
        Beschreibung, Regelwerk, Hinweise und Kontakt stehen in der vollen Maske (Grunddaten, Einstellungen, Kontakt). Bankdaten erscheinen nie öffentlich:
        was Vereine zur Zahlung wissen sollen, steht unter „Zahlungsweise“.
        {eigenerKnopf && ' Dieser Reiter speichert mit dem eigenen Knopf ganz unten.'}</p>

      <h3>Veranstalter und Ort</h3>
      <div className="tw-raster">
        {eingabe('veranstalter_name', 'Veranstalter', { placeholder: 'leer: Name des Verbands' })}
        {eingabe('ausrichter_name', 'Ausrichter (Verein vor Ort)')}
        {eingabe('wettkampfleitung', 'Wettkampfleitung', {}, 'Name erscheint öffentlich.')}
        {eingabe('halle_name', 'Halle', { placeholder: 'z. B. Dreifachturnhalle am Schulzentrum' }, 'Leer: es gilt „Halle / Ort“ aus den Stammdaten.')}
        {eingabe('einlass_zeit', 'Einlass', { type: 'time' })}
        {text('parken', 'Parken')}
        {text('oepnv', 'Bus und Bahn')}
        {text('barrierefrei', 'Barrierefreiheit')}
      </div>

      <h3>Zeitplan</h3>
      <p className="ta-klein">Programmpunkte für die öffentliche Seite. Der genaue Plan je Matte entsteht weiter im Zeitplan.</p>
      {listen.programm.map((p, i) => (
        <div key={i} className="au-ed-zeile au-ed-programm">
          <input type="date" aria-label="Tag" value={p.tag} onChange={(e) => zeile('programm', i, 'tag', e.target.value)} />
          <input type="time" aria-label="von" value={p.von} onChange={(e) => zeile('programm', i, 'von', e.target.value)} />
          <input type="time" aria-label="bis" value={p.bis} onChange={(e) => zeile('programm', i, 'bis', e.target.value)} />
          <input aria-label="Titel" placeholder="Titel, z. B. Check-in und Waage" value={p.titel} onChange={(e) => zeile('programm', i, 'titel', e.target.value)} />
          <select aria-label="Art" value={p.art} onChange={(e) => zeile('programm', i, 'art', e.target.value)}>
            {Object.entries(ART_TEXT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input aria-label="Fläche" placeholder="Fläche (freiwillig)" value={p.flaeche} onChange={(e) => zeile('programm', i, 'flaeche', e.target.value)} />
          <input aria-label="Beschreibung" placeholder="Beschreibung (freiwillig)" className="au-ed-lang" value={p.text} onChange={(e) => zeile('programm', i, 'text', e.target.value)} />
          {listenKnoepfe('programm', i, listen.programm.length)}
        </div>
      ))}
      <button type="button" className="ta-btn ta-btn-leise" onClick={() => dazu('programm')}>Programmpunkt hinzufügen</button>

      <h3>Bezahlen und Abmelden</h3>
      <div className="tw-raster">
        {text('zahlungsweise', 'Zahlungsweise', 'Erscheint öffentlich, z. B. „Überweisung über die Vereinsrechnung, keine Barzahlung vor Ort“.')}
        {eingabe('zahlungsfrist', 'Zahlungsfrist', { placeholder: 'z. B. 14 Tage nach Rechnung' })}
        {text('abmelde_regel', 'Abmeldung und Erstattung')}
        {eingabe('ummeldegebuehr', 'Ummeldegebühr (€)', { type: 'number', min: 0, step: 0.5 }, 'Wechsel der Klasse nach Meldeschluss.')}
        {eingabe('protestgebuehr', 'Protestgebühr (€)', { type: 'number', min: 0, step: 0.5 })}
      </div>

      <h3>Teilnahme, Haftung, Datenschutz</h3>
      <div className="tw-raster">
        {text('startberechtigung', 'Startberechtigt')}
        {eingabe('pass_pflicht', 'Pass oder Lizenz')}
        {eingabe('altersnachweis', 'Altersnachweis')}
        <div className="tw-feld tw-breit">
          <span className="au-ed-mit-knopf">Haftung, Versicherung, Datenschutz
            <button type="button" className="ta-btn" onClick={vorlageHolen}>Aus Verbandsvorlage übernehmen</button></span>
        </div>
        {text('haftung_text', 'Haftung', null, 3)}
        {text('versicherung_text', 'Versicherung', null, 2)}
        {text('datenschutz_text', 'Datenschutz und Fotos', null, 3)}
        {text('kampfrichter_regel', 'Kampfrichter je Verein')}
        {eingabe('kampfrichter_ausfallgebuehr', 'Ausfallgebühr je fehlendem Kampfrichter (€)', { type: 'number', min: 0, step: 0.5 })}
        {text('coach_regel', 'Coaches')}
      </div>

      <h3>Kampfzeiten</h3>
      {listen.kampfzeiten.map((k, i) => (
        <div key={i} className="au-ed-zeile au-ed-drei">
          <input aria-label="Disziplin" placeholder="Disziplin, z. B. Pointfighting" value={k.disziplin} onChange={(e) => zeile('kampfzeiten', i, 'disziplin', e.target.value)} />
          <input aria-label="Klasse" placeholder="Klasse, z. B. Jugend 12–14" value={k.klasse} onChange={(e) => zeile('kampfzeiten', i, 'klasse', e.target.value)} />
          <input aria-label="Zeit" placeholder="z. B. 2 × 1:30 min" value={k.zeit} onChange={(e) => zeile('kampfzeiten', i, 'zeit', e.target.value)} />
          {listenKnoepfe('kampfzeiten', i, listen.kampfzeiten.length)}
        </div>
      ))}
      <button type="button" className="ta-btn ta-btn-leise" onClick={() => dazu('kampfzeiten')}>Kampfzeit hinzufügen</button>

      <h3>Schutzausrüstung</h3>
      {listen.schutz.map((s, i) => (
        <div key={i} className="au-ed-zeile au-ed-zwei">
          <input aria-label="Disziplin" placeholder="Disziplin" value={s.disziplin} onChange={(e) => zeile('schutz', i, 'disziplin', e.target.value)} />
          <input aria-label="Ausrüstung" placeholder="z. B. Kopfschutz, Zahnschutz, Handschuhe" value={s.ausruestung} onChange={(e) => zeile('schutz', i, 'ausruestung', e.target.value)} />
          {listenKnoepfe('schutz', i, listen.schutz.length)}
        </div>
      ))}
      <button type="button" className="ta-btn ta-btn-leise" onClick={() => dazu('schutz')}>Disziplin hinzufügen</button>

      <h3>Vor Ort und Übernachtung</h3>
      <div className="tw-raster">
        {text('zuschauer_eintritt', 'Eintritt für Zuschauer')}
        {text('verpflegung', 'Verpflegung')}
      </div>
      <h4>Übernachtung</h4>
      {listen.hotels.map((h, i) => (
        <div key={i} className="au-ed-zeile au-ed-hotel">
          <input aria-label="Hotel" placeholder="Name" value={h.name} onChange={(e) => zeile('hotels', i, 'name', e.target.value)} />
          <input aria-label="Entfernung" placeholder="z. B. 1,2 km" value={h.entfernung} onChange={(e) => zeile('hotels', i, 'entfernung', e.target.value)} />
          <input aria-label="Hinweis" placeholder="Hinweis, z. B. Stichwort" value={h.hinweis} onChange={(e) => zeile('hotels', i, 'hinweis', e.target.value)} />
          <input aria-label="Link" placeholder="https://…" value={h.link} onChange={(e) => zeile('hotels', i, 'link', e.target.value)} />
          {listenKnoepfe('hotels', i, listen.hotels.length)}
        </div>
      ))}
      <button type="button" className="ta-btn ta-btn-leise" onClick={() => dazu('hotels')}>Hotel hinzufügen</button>

      <h3>PDF-Anhänge</h3>
      <p className="ta-klein">Nur PDF, höchstens 10 MB, bis 20 Anhänge. „Öffentlich“ heißt: auf der Turnierseite zum Herunterladen, sobald das Turnier veröffentlicht ist. Die Kurzfassung der Ausschreibung als PDF entsteht von selbst.</p>
      {dokumente.length > 0 && (
        <ul className="au-ed-doks">
          {dokumente.map((d) => (
            <li key={d.id}>
              <input aria-label="Titel" defaultValue={d.titel} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== d.titel) dokAendern(d, { titel: e.target.value }); }} />
              <span className="ta-klein">{groesse(d.groesse)}</span>
              <label className="au-ed-check"><input type="checkbox" checked={d.oeffentlich} onChange={(e) => dokAendern(d, { oeffentlich: e.target.checked })} /> öffentlich</label>
              <button type="button" className="ta-btn ta-btn-leise" onClick={() => dokAnsehen(d)}>Ansehen</button>
              <button type="button" className="ta-btn ta-btn-gefahr" onClick={() => dokLoeschen(d)}>Löschen</button>
            </li>
          ))}
        </ul>
      )}
      <div className="au-ed-zeile au-ed-upload">
        <input key={uploadSchluessel} type="file" accept="application/pdf,.pdf" aria-label="PDF-Datei" onChange={(e) => setUpload((u) => ({ ...u, datei: e.target.files?.[0] || null }))} />
        <input aria-label="Titel des Anhangs" placeholder="Titel, z. B. Wettkampfordnung 2027" value={upload.titel} onChange={(e) => setUpload((u) => ({ ...u, titel: e.target.value }))} />
        <label className="au-ed-check"><input type="checkbox" checked={upload.oeffentlich} onChange={(e) => setUpload((u) => ({ ...u, oeffentlich: e.target.checked }))} /> öffentlich</label>
        <button type="button" className="ta-btn" disabled={busy || !upload.datei} onClick={hochladen}>Hochladen</button>
      </div>

      {fehler && <div className="ta-meldung ta-meldung-fehler" role="alert">{fehler}</div>}
      {hinweis && <div className="ta-meldung ta-meldung-ok" role="status">{hinweis}</div>}
      {eigenerKnopf && (
        <div className="ta-knoepfe tw-fuss">
          <a className="ta-btn ta-btn-leise" href={`/event/${turnierId}`} target="_blank" rel="noopener noreferrer">Öffentliche Seite ansehen</a>
          <button type="button" className="ta-btn ta-btn-haupt" disabled={busy} onClick={speichern}>{busy ? 'Speichert …' : 'Ausschreibung speichern'}</button>
        </div>
      )}
    </div>
  );
});

export default AusschreibungEditor;
